/**
 * App Store - Global Alpine.js store for application-wide state
 *
 * Provides centralized state for:
 * - Network status (online/offline)
 * - PWA install prompt management
 * - Toast notification system
 * - Pending offline sync tracking
 *
 * Usage:
 *   - Access from any Alpine component: $store.app.isOnline
 *   - Show toast: $store.app.showToast('Message', 'success')
 *   - Trigger PWA install: $store.app.promptInstall()
 */

import { captureClientError } from '../utils/capture-error';
import { trackEvent } from '../utils/analytics';
import {
  getQueueCount,
  syncPendingSelectionsNow,
  onOnlineStatusChange,
} from '../offline-storage';

/**
 * Initialize the app store
 * @param {typeof Alpine} Alpine - The Alpine.js instance
 */
export function initAppStore(Alpine) {
  Alpine.store('app', {
    // =========================================================================
    // Network State
    // =========================================================================
    isOnline: navigator.onLine,
    pendingSyncCount: 0,
    syncing: false,

    // =========================================================================
    // PWA Install State
    // =========================================================================
    /** @type {BeforeInstallPromptEvent|null} */
    installPrompt: null,
    canInstall: false,
    isStandalone:
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true,

    // =========================================================================
    // User State
    // =========================================================================
    /** @type {Object|null} */
    user: null,
    isAuthenticated: false,

    // =========================================================================
    // Toast Notifications
    // =========================================================================
    /** @type {Array<{id: number, message: string, type: string, visible: boolean}>} */
    toasts: [],
    _toastId: 0,
    /** @type {Map<number, number>} Map of toast ID to timeout ID for cleanup */
    _toastTimeouts: new Map(),

    // =========================================================================
    // Private Cleanup
    // =========================================================================
    _cleanupFns: [],

    // =========================================================================
    // Initialization
    // =========================================================================

    /**
     * Initialize the store - called automatically by Alpine
     */
    init() {
      // Network status listeners
      const cleanupOnlineStatus = onOnlineStatusChange((online) => {
        this.isOnline = online;
        if (online) {
          this.showToast('Back online', 'success', 3000);
          this.requestSync();
        } else {
          this.showToast('You are offline', 'warning', 5000);
        }
      });
      this._cleanupFns.push(cleanupOnlineStatus);

      // Load initial pending count
      this._updatePendingCount();

      // PWA install prompt capture
      const handleInstallPrompt = (event) => {
        // Prevent the mini-infobar from appearing
        event.preventDefault();
        // Store the event for later use
        this.installPrompt = event;
        this.canInstall = true;
        trackEvent('pwa_install_prompt');
      };
      window.addEventListener('beforeinstallprompt', handleInstallPrompt);
      this._cleanupFns.push(() => {
        window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      });

      // Track when app is installed
      const handleAppInstalled = () => {
        this.canInstall = false;
        this.installPrompt = null;
        this.isStandalone = true;
        this.showToast('App installed successfully!', 'success');
        trackEvent('pwa_installed');
      };
      window.addEventListener('appinstalled', handleAppInstalled);
      this._cleanupFns.push(() => {
        window.removeEventListener('appinstalled', handleAppInstalled);
      });

      // Listen for sync complete events from offline storage
      const handleSyncComplete = (event) => {
        const { success, failed } = event.detail || {};
        if (success > 0) {
          this.showToast(
            `Synced ${success} change${success > 1 ? 's' : ''}`,
            'success'
          );
        }
        if (failed > 0) {
          this.showToast(
            `Failed to sync ${failed} change${failed > 1 ? 's' : ''}`,
            'error'
          );
        }
        this._updatePendingCount();
      };
      window.addEventListener('offline-sync-complete', handleSyncComplete);
      this._cleanupFns.push(() => {
        window.removeEventListener('offline-sync-complete', handleSyncComplete);
      });

      // Load user from meta tag if available
      this._loadUserFromMeta();
    },

    /**
     * Cleanup function - not typically called but available
     */
    destroy() {
      this._cleanupFns.forEach((fn) => fn());
      this._cleanupFns = [];

      // Clear all tracked toast timeouts (MED-12 fix)
      this._toastTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      this._toastTimeouts.clear();
    },

    // =========================================================================
    // Toast Notification Methods
    // =========================================================================

    /**
     * Show a toast notification
     * @param {string} message - The message to display
     * @param {'success'|'error'|'warning'|'info'} type - Toast type
     * @param {number} duration - Duration in ms (0 = no auto-dismiss)
     * @returns {number} The toast ID
     */
    showToast(message, type = 'info', duration = 5000) {
      const id = ++this._toastId;
      const toast = { id, message, type, visible: true };

      this.toasts.push(toast);

      // Auto-dismiss after duration and track timeout for cleanup (MED-12 fix)
      if (duration > 0) {
        const timeoutId = setTimeout(() => this.dismissToast(id), duration);
        this._toastTimeouts.set(id, timeoutId);
      }

      return id;
    },

    /**
     * Dismiss a specific toast
     * @param {number} id - The toast ID to dismiss
     */
    dismissToast(id) {
      // Clear tracked timeout if it exists (MED-12 fix)
      const timeoutId = this._toastTimeouts.get(id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this._toastTimeouts.delete(id);
      }

      const toast = this.toasts.find((t) => t.id === id);
      if (toast) {
        toast.visible = false;
        // Remove from array after animation
        setTimeout(() => {
          this.toasts = this.toasts.filter((t) => t.id !== id);
        }, 300);
      }
    },

    /**
     * Dismiss all toasts
     */
    dismissAllToasts() {
      this.toasts.forEach((t) => (t.visible = false));
      setTimeout(() => {
        this.toasts = [];
      }, 300);
    },

    // =========================================================================
    // PWA Install Methods
    // =========================================================================

    /**
     * Prompt the user to install the PWA
     * @returns {Promise<boolean>} True if accepted
     */
    async promptInstall() {
      if (!this.installPrompt) {
        return false;
      }

      try {
        // Show the install prompt
        this.installPrompt.prompt();

        // Wait for user response
        const result = await this.installPrompt.userChoice;

        // Clear the prompt (can only be used once)
        this.installPrompt = null;
        this.canInstall = false;

        return result.outcome === 'accepted';
      } catch (error) {
        captureClientError(error, {
          component: 'app-store',
          action: 'prompt-install',
        });
        console.error('[AppStore] Install prompt failed:', error);
        return false;
      }
    },

    // =========================================================================
    // Offline Sync Methods
    // =========================================================================

    /**
     * Request sync of pending offline changes
     */
    async requestSync() {
      if (this.syncing || !this.isOnline || this.pendingSyncCount === 0) {
        return;
      }

      this.syncing = true;

      try {
        const result = await syncPendingSelectionsNow();
        await this._updatePendingCount();

        // Dispatch event for components listening
        window.dispatchEvent(
          new CustomEvent('offline-sync-complete', {
            detail: result,
          })
        );
      } catch (error) {
        console.error('[AppStore] Sync failed:', error);
        this.showToast('Sync failed. Will retry later.', 'error');
      } finally {
        this.syncing = false;
      }
    },

    // =========================================================================
    // User Methods
    // =========================================================================

    /**
     * Set the authenticated user info
     * @param {Object|null} user - User data or null to clear
     */
    setUser(user) {
      this.user = user;
      this.isAuthenticated = user !== null;
    },

    // =========================================================================
    // Private Helpers
    // =========================================================================

    /**
     * Update the pending sync count from IndexedDB
     * @private
     */
    async _updatePendingCount() {
      try {
        this.pendingSyncCount = await getQueueCount();
      } catch (error) {
        if (error?.name === 'SecurityError') {
          console.warn(
            '[AppStore] IndexedDB unavailable — using localStorage fallback'
          );
        } else {
          captureClientError(error, {
            component: 'app-store',
            action: 'update-pending-count',
          });
          console.error('[AppStore] Failed to get pending count:', error);
        }
      }
    },

    /**
     * Load user from meta tag if present
     * @private
     */
    _loadUserFromMeta() {
      const userMeta = document.querySelector('meta[name="user-data"]');
      if (userMeta) {
        try {
          const userData = JSON.parse(userMeta.content);
          this.setUser(userData);
        } catch (e) {
          // No user data or invalid JSON
        }
      } else {
        // Check for auth indicator
        const authMeta = document.querySelector('meta[name="authenticated"]');
        this.isAuthenticated = authMeta?.content === 'true';
      }
    },
  });
}
